import Template from './Template30.hbs';
import WordSelection from 'views/components/taskGroups/tasks/wordSelection/WordSelection';
import ButtonFragmentTemplate from 'views/components/taskGroups/tasks/wordSelection/ButtonFragment.hbs';
import QuickSelect from 'views/components/quickSelect/QuickSelect.svelte';
import GapTemplate from 'views/components/taskGroups/tasks/wordSelection/Gap.hbs';
import FragmentTooltipTemplate from 'views/components/taskGroups/tasks/wordSelection/FragmentTooltip.hbs';
import WordSelectionStyles from 'views/components/taskGroups/tasks/wordSelection/WordSelection.scss';

export default class Template30 extends WordSelection {

    get events() {
        return {
            'click .js-click-fragment': 'onClickInlineFragment',
        }
    }

    get textToSpeechURL() {
        // Create URL query parameter with the order of each gap option, separated
        // per gap with a "|" character.
        const gapIndices = this.model.get('task_info_json').text.reduce((m, fragment) => {
            if (fragment instanceof Object) {
                m.push(_.pluck(fragment.answers, 'index').join())
            }
            return m
        }, [])
        return `/tasks/synthesize/${this.model.id}.json?gaps=${gapIndices.join('|')}`
    }

    initialize({
        task_view,
        isPresentation,
    }) {

        _.bindAll(
            this,
            'lockAnswer'
        );

        this.taskView = task_view
        this.isPresentation = isPresentation
        this.useDropdownInput = this.model.get('task_info_json').options &&
            this.model.get('task_info_json').options.useDropdownInput &&
            APPLICATION !== 'author'

        this.prepareData();

        this.renderTask()

    }

    /**
     * This method will render the template of the task. It will be
     * overwritten with an empty method in /views/components/taskGroups/tasks/Task.js
     * if only the answer view is necessary
     */
    renderTask() {

        this.setElement(Template({
            Styles: WordSelectionStyles
        }));

        // Reduce gap templates and plain text into a single string and append
        // it to the DOM. This makes it possible for the browser to correct
        // with any invalid HTML automatically without having to normalize
        // and parse each individual fragment, which can result in errors.
        this.$el.append(this.fragments.reduce((content, fragment) => {
            if (fragment instanceof Object) {
                return content + GapTemplate({
                    fragmentID: fragment.id,
                    Styles: WordSelectionStyles
                });
            }
            return content + this.insertPlainText(fragment);
        }, ''))

        // Create fragment element for each fragment.
        // Add an highlight if it already has been selected.
        for (const fragment of this.fragments) {

            if (fragment instanceof Object === false) {
                continue
            }
            const sortedAnswers = this.sortAnswerVariants(fragment.answers)

            const existingAnswer = _.findWhere(this.JSONAnswer, {id: fragment.id})

            const gapElement = this.$(`.js-gap[data-gap-id=${fragment.id}]`)

            if (this.useDropdownInput) {
                gapElement
                    .addClass([
                        WordSelectionStyles['dropdown-holder'],
                        'js-fragment'
                    ]).attr('data-fragment-index', fragment.id)

                this.addSvelteChildView(gapElement, QuickSelect, {
                    items: [{
                        label: '\u00A0',
                        value: -1
                    }].concat(sortedAnswers.map((answer, index) => {
                        return {
                            label: answer.text,
                            value: index
                        }
                    })),
                    defaultValue: _.findIndex(sortedAnswers, {text: existingAnswer?.answer.text}),
                    callback: (selectedItem) => {
                        this.selectFragment(undefined, fragment.id, selectedItem.value)
                    }
                })

                continue
            }

            gapElement.append(sortedAnswers.reduce((content, answer) => {
                let isSelected = false
                if (existingAnswer) {
                    isSelected = existingAnswer.answer.text === answer.text
                }
                // Get template we just added by searching for its data-gap-id and
                // append a fragment template to it.
                return content + ButtonFragmentTemplate({
                    Styles: WordSelectionStyles,
                    fragment: answer.text,
                    fragmentIndex: fragment.id,
                    fragmentStatus: isSelected ? WordSelectionStyles['fragment--selected'] : undefined
                })
            }, ''))

        }

    }

    /**
     * When clicking on a fragment element react by adding/removing the selection status from the
     * response and toggling the correct styling.
     *
     * @param {Element} fragmentElement    interacted fragment element
     * @param {Number}  fragmentIndex      index of fragment/gap
     * @param {Number}  gapOptionIndex     index of option within gap.
     */
    selectFragment(fragmentElement, fragmentIndex, gapOptionIndex) {
        this.JSONAnswer = _.clone(this.JSONAnswer)

        const existingFragment = _.findWhere(this.fragments, {id: fragmentIndex})
        const answer = {
            id: fragmentIndex,
            answer: _.omit(
                existingFragment.answers[gapOptionIndex],
                'correct'
            )
        }

        const existingAnswerIndex = _.findIndex(this.JSONAnswer, {id: fragmentIndex})

        if (existingAnswerIndex >= 0) {
            if (gapOptionIndex === -1 || _.isEqual(this.JSONAnswer[existingAnswerIndex], answer)) {
                this.JSONAnswer.splice(existingAnswerIndex, 1)
            } else {
                this.JSONAnswer[existingAnswerIndex] = answer
                fragmentElement?.classList.add(WordSelectionStyles['fragment--selected'])
            }
        } else {
            this.JSONAnswer.push(answer)
            fragmentElement?.classList.add(WordSelectionStyles['fragment--selected'])
        }

        // Save the new answer.
        this.saveAnswer()
    }

    onClickInlineFragment(e) {
        this.stopAllEvents(e)
        // Refuse interaction when showing answers.
        if (!this.$el.hasClass('showAnswers')) {
            const fragmentElement = e.currentTarget
            const fragmentIndex = parseInt(fragmentElement.dataset.fragmentIndex)
            const gapElement = this.$('.js-gap[data-gap-id=' + fragmentIndex + ']')

            // Remove selection status styling from all fragments within the current gap.
            gapElement.find('.js-fragment').removeClass(WordSelectionStyles['fragment--selected'])

            const gapOptionIndex = gapElement.find('.js-fragment').index(fragmentElement)

            this.selectFragment(fragmentElement, fragmentIndex, gapOptionIndex)
        }
    }

    /**
     * Show the correct answer to the task.
     */
    showAnswer() {

        for (const fragment of this.fragments) {

            // Skip non selectable fragments (words)
            if (fragment instanceof Object === false) {
                continue
            }

            const givenGapAnswer = _.findWhere(this.JSONAnswer, {id: fragment.id})

            if (this.useDropdownInput) {

                const fragmentElement = this.el.querySelector('.js-fragment[data-fragment-index="' + fragment.id + '"]')
                fragmentElement.classList.add(WordSelectionStyles['fragment--show-answer'])

                if (givenGapAnswer) {

                    // Find the given answer in fragment.answers to check if it is correct
                    const fragmentAnswerSelected = fragment.answers.find(
                        answer => answer.text === givenGapAnswer.answer.text
                    )

                    // If the given answer is correct, make it green and go to the next fragment
                    if (fragmentAnswerSelected.correct) {
                        fragmentElement.classList.add(WordSelectionStyles['fragment--correct'])
                        continue
                    }
                }

                // If there is no given answer, or the given answer is not correct, make it red
                // and show the correct answer in a tooltip. Then go to the next fragment.
                fragmentElement.classList.add(WordSelectionStyles['fragment--incorrect'])
                fragmentElement.append($(FragmentTooltipTemplate({
                    Styles: WordSelectionStyles,
                    tooltipText: _.findWhere(fragment.answers, {correct: true})?.text
                }))[0])
                continue
            }

            const fragmentElements = this.$('.js-fragment[data-fragment-index=' + fragment.id + ']')

            _.each(fragmentElements, (fragmentElement, fragmentIndex) => {

                fragmentElement.classList.add(WordSelectionStyles['fragment--show-answer'])

                const gapAnswer = fragment.answers[fragmentIndex]

                if (
                    gapAnswer?.correct &&
                    givenGapAnswer && givenGapAnswer.answer.text === gapAnswer.text
                ) {
                    fragmentElement.classList.add(WordSelectionStyles['fragment--correct'])
                } else if (
                    (
                        // If the gapAnswer iteratee is correct
                        gapAnswer?.correct &&

                        // But the user hasn't filled an answer
                        !givenGapAnswer
                    ) ||
                    (
                        // Or if the gapAnswer iteratee is correct
                        gapAnswer?.correct &&

                        // And the user has filled a gap answer
                        givenGapAnswer &&

                        // But the answer of the user doesn't equal the correct answer
                        givenGapAnswer.answer.text !== gapAnswer.text
                    )
                ) {
                    if (APPLICATION === 'author') {
                        fragmentElement.classList.add(WordSelectionStyles['fragment--selected'])
                    } else {
                        fragmentElement.classList.add(WordSelectionStyles['fragment--missing-answer'])
                    }
                } else if (
                    APPLICATION !== 'author' && (
                        gapAnswer === undefined || (
                            !gapAnswer.correct &&
                            givenGapAnswer &&
                            givenGapAnswer.answer.text === gapAnswer.text
                        )
                    )
                ) {
                    fragmentElement.classList.add(WordSelectionStyles['fragment--incorrect'])
                }
            })

        }

    }

    showAuthorAnswer() {
        this.showAnswer()
    }

    /**
     * Sort fragments alphabetical or reverse alphabetical if a sequence mode option is set in task_info_json
     *
     * @param {Object} fragment     unsorted fragments
     * @returns {Object}            sorted fragments
     */
    sortAnswerVariants(fragment) {
        const sequenceMode = this.model.get('task_info_json').options.sequenceMode

        const isReverse = sequenceMode === 'alphabetical-reverse'
        if (isReverse || sequenceMode === 'alphabetical') {
            fragment.sort((a, b) => {
                const index = a.text.localeCompare(b.text, window.app_version.language, {sensitivity: 'accent', numeric: true})
                return isReverse ? (0 - index) : index
            })
        }

        return fragment
    }

    /**
     * Hide the correct answer to the task.
     */
    hideAnswer() {
        this.$('.js-fragment').removeClass([
            WordSelectionStyles['fragment--show-answer'],
            WordSelectionStyles['fragment--correct'],
            WordSelectionStyles['fragment--missing-answer'],
            WordSelectionStyles['fragment--incorrect']
        ])
        if (this.useDropdownInput) {
            this.$('.js-answer-tooltip').remove()
        }
    }

    lockAnswer() {
        this.$('.js-fragment').addClass(WordSelectionStyles['fragment--locked'])
        this.undelegateEvents();
        if (this.useDropdownInput) {
            this.el.querySelectorAll('select')?.forEach(el => el.disabled = true)
        } else {
            this.$('button').prop('disabled', true)
        }
    }

    unlockAnswer() {
        this.$('.js-fragment').removeClass(WordSelectionStyles['fragment--locked'])
        this.delegateEvents();
        if (this.useDropdownInput) {
            this.el.querySelectorAll('select')?.forEach(el => el.disabled = false)
        } else {
            this.$('button').prop('disabled', false)
        }
    }

}
